/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { transcribeAudio } from "../api/threads.js";
import type { ModelOption } from "../shared/model-option.js";
import type { PromptAttachment } from "../shared/prompt-attachment.js";
import { AppAttachment } from "./app-attachment.js";
import { AppButton } from "./app-button.js";
import { AppModelPicker } from "./app-model-picker.js";
import { IconArrowUp } from "./icons/arrow-up.js";
import { IconFile } from "./icons/file.js";
import { IconMic } from "./icons/mic.js";
import { IconPlus } from "./icons/plus.js";
import { IconStop } from "./icons/stop.js";
import { IconX } from "./icons/x.js";

const styles = css`
  :host {
    display: flex;
    flex-direction: column;
    max-width: 870px;
    width: 100%;
    height: fit-content;
    max-height: none;
    padding: 8px 0 12px;
  }

  :host([hidden]) {
    display: none;
  }

  [hidden] {
    display: none !important;
  }

  form {
    background: var(--prompt-bg, #212121);
    border: 1px solid var(--prompt-border, #333333);
    border-radius: 24px;
    box-shadow: var(--prompt-shadow, none);
    box-sizing: border-box;
    display: grid;
    gap: 8px;
    min-height: 112px;
    padding: 12px 20px 10px;
    transition:
      border-color 140ms ease,
      box-shadow 140ms ease;
  }

  form:focus-within {
    border-color: var(--prompt-focus-border, #3c3c3c);
    box-shadow: 0 0 0 3px var(--prompt-ring, rgb(255 255 255 / 7%));
  }

  textarea {
    background: transparent;
    border: 0;
    color: var(--prompt-fg, #d4d4d4);
    font: inherit;
    font-size: 1rem;
    line-height: 1.4;
    max-height: 220px;
    min-height: 44px;
    min-width: 0;
    outline: none;
    overflow-y: auto;
    padding: 0 2px;
    resize: none;
    width: 100%;
  }

  textarea::placeholder {
    color: var(--prompt-muted, #747474);
  }

  textarea:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .attachments {
    display: flex;
    gap: 8px;
    margin: 0 0 8px;
    max-width: 100%;
    overflow-x: auto;
    padding: 2px 20px;
    scroll-snap-type: x proximity;
    scrollbar-width: thin;
  }

  .attachments app-attachment {
    flex: 0 0 auto;
    max-width: min(240px, calc(100vw - 80px));
    scroll-snap-align: start;
  }

  .attachment-file-icon { color: var(--muted-foreground); height: 18px; width: 18px; }
  .attachment-remove { --foreground: var(--muted-foreground); }
  .attachment-remove:hover { --foreground: var(--foreground); }

  .toolbar {
    align-items: center;
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr auto auto;
    min-height: 32px;
  }

  .right-actions {
    align-items: center;
    display: flex;
  }

  .attachment { --secondary: transparent; --secondary-foreground: var(--prompt-control-fg, #efefef); --secondary-hover: var(--prompt-control-hover-bg, #303030); }
  .primary-action { --primary: var(--accent); --primary-foreground: var(--accent-foreground); --primary-hover: color-mix(in oklab, var(--accent) 82%, black); }
  .primary-action.ready { --primary: var(--accent); --primary-foreground: var(--accent-foreground); --primary-hover: color-mix(in oklab, var(--accent) 82%, black); }
  .primary-action.recording { --primary: var(--prompt-record-bg, #b91c1c); --primary-foreground: #ffffff; --primary-hover: var(--prompt-record-hover-bg, #dc2626); animation: prompt-pulse 1.2s ease-in-out infinite; }
  .primary-action:has(app-button[disabled]) { opacity: 0.5; pointer-events: none; }

  @keyframes prompt-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }

  .sr-only {
    border: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  @media (max-width: 640px) {
    :host {
      max-width: 100%;
    }

    form {
      border-radius: 28px;
      gap: 14px;
      min-height: 108px;
      padding: 14px 16px 12px;
      width: 100%;
    }

    .attachments { padding-inline: 16px; }

    .toolbar {
      gap: 12px;
    }

    textarea { font-size: 1rem; min-height: 44px; }
  }
`;

function submitPrompt(host: HTMLElement, textarea: HTMLTextAreaElement, uploadPending = false): boolean {
  const value = textarea.value.trim();
  if (!value || textarea.disabled || uploadPending) return false;
  textarea.value = "";
  textarea.style.height = "";
  const model = host.getAttribute("data-selected-model") ?? "";
  host.dispatchEvent(
    new CustomEvent("prompt-submit", {
      bubbles: true,
      composed: true,
      detail: { value, model: model || null },
    }),
  );
  return true;
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  if (size < 10 * 1024 * 1024) return `${Math.round(size / (1024 * 1024) * 10) / 10} MB`;
  return `${Math.round(size / (1024 * 1024))} MB`;
}

function attachmentType(name: string): string {
  const normalized = name.toLowerCase();
  if (normalized.endsWith(".pdf")) return "PDF";
  if (normalized.endsWith(".csv")) return "CSV";
  if (normalized.endsWith(".zip")) return "ZIP";
  if (normalized.endsWith(".txt")) return "TXT";
  if (normalized.endsWith(".md")) return "MD";
  if (normalized.endsWith(".json")) return "JSON";
  if (normalized.endsWith(".png")) return "PNG";
  if (normalized.endsWith(".jpg") || normalized.endsWith(".jpeg")) return "JPG";
  if (normalized.endsWith(".webp")) return "WEBP";
  return "File";
}

function attachmentDescription(attachment: PromptAttachment): string {
  if (attachment.state === "uploading") return `Uploading · ${formatFileSize(attachment.size)}`;
  if (attachment.state === "error") return "Upload failed. Remove and try again.";
  return `${attachmentType(attachment.name)} · ${formatFileSize(attachment.size)}`;
}

function hasPendingUpload(attachments: PromptAttachment[]): boolean {
  return attachments.some((attachment) => attachment.state === "uploading");
}

function emitModelChange(host: HTMLElement, value: string): void {
  if (value) {
    host.setAttribute("data-selected-model", value);
  } else {
    host.removeAttribute("data-selected-model");
  }
  host.dispatchEvent(
    new CustomEvent("model-change", {
      bubbles: true,
      composed: true,
      detail: { value },
    }),
  );
}

function emitPromptError(host: HTMLElement, message: string): void {
  host.dispatchEvent(
    new CustomEvent("prompt-error", { bubbles: true, composed: true, detail: { message } }),
  );
}

function insertTranscript(root: ShadowRoot, text: string): string {
  const textarea = root.querySelector<HTMLTextAreaElement>("textarea");
  if (!textarea || !text) return textarea?.value ?? "";
  const existing = textarea.value.trim();
  textarea.value = existing ? `${existing} ${text}` : text;
  textarea.style.height = "";
  textarea.style.height = `${Math.min(textarea.scrollHeight, 220)}px`;
  textarea.focus();
  return textarea.value;
}

export class AppPromptInput extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppPromptInput)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["disabled","data-disabled","running","data-running","placeholder","data-placeholder","models","data-models","selectedmodel","data-selected-model","selectedmodellabel","data-selected-model-label","selectedmodelreasoningeffort","data-selected-model-reasoning-effort","attachments","data-attachments"];

  #disabled = false;
  #running = false;
  #placeholder = "Send a message";
  #models = [];
  #selectedModel = "";
  #selectedModelLabel = "Choose model";
  #selectedModelReasoningEffort = "";
  #attachments = [];
  #recording;
  #transcribing;
  #draft;
  #recorder;
  #chunks;
  #disabledSet = false;
  #runningSet = false;
  #placeholderSet = false;
  #modelsSet = false;
  #selectedModelSet = false;
  #selectedModelLabelSet = false;
  #selectedModelReasoningEffortSet = false;
  #attachmentsSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #attr0;
  #attr1;
  #attr2;
  #attr3;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #attrLast4;
  #attrLast5;
  #attrLast6;
  #attrLast7;
  #attrLast8;
  #attrLast9;
  #attrLast10;
  #eventContexts = {};
  #input = (() => { const self = this; return { get current() { return self.#root ? self.#root.querySelector('[data-argon-ref="input"]') : null; } }; })();
  #hooksLive = false;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get disabled() {
    return this.#disabled;
  }

  set disabled(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#disabled, next)) return;
    this.#disabled = next;
    this.#disabledSet = true;
    this.#updateBindings('disabled');
  }

  get running() {
    return this.#running;
  }

  set running(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#running, next)) return;
    this.#running = next;
    this.#runningSet = true;
    this.#updateBindings('running');
  }

  get placeholder() {
    return this.#placeholder;
  }

  set placeholder(value) {
    const raw = value ?? "Send a message";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#placeholder, next)) return;
    this.#placeholder = next;
    this.#placeholderSet = true;
    this.#updateBindings('placeholder');
  }

  get models() {
    return this.#models;
  }

  set models(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#models, next)) return;
    this.#models = next;
    this.#modelsSet = true;
    this.#updateBindings('models');
  }

  get selectedModel() {
    return this.#selectedModel;
  }

  set selectedModel(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#selectedModel, next)) return;
    this.#selectedModel = next;
    this.#selectedModelSet = true;
    this.#updateBindings('selectedModel');
  }

  get selectedModelLabel() {
    return this.#selectedModelLabel;
  }

  set selectedModelLabel(value) {
    const raw = value ?? "Choose model";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#selectedModelLabel, next)) return;
    this.#selectedModelLabel = next;
    this.#selectedModelLabelSet = true;
    this.#updateBindings('selectedModelLabel');
  }

  get selectedModelReasoningEffort() {
    return this.#selectedModelReasoningEffort;
  }

  set selectedModelReasoningEffort(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#selectedModelReasoningEffort, next)) return;
    this.#selectedModelReasoningEffort = next;
    this.#selectedModelReasoningEffortSet = true;
    this.#updateBindings('selectedModelReasoningEffort');
  }

  get attachments() {
    return this.#attachments;
  }

  set attachments(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#attachments, next)) return;
    this.#attachments = next;
    this.#attachmentsSet = true;
    this.#updateBindings('attachments');
  }

  #setRecording(value) {
    if (Object.is(this.#recording, value)) return;
    this.#recording = value;
    this.#updateBindings('recording');
  }

  #setTranscribing(value) {
    if (Object.is(this.#transcribing, value)) return;
    this.#transcribing = value;
    this.#updateBindings('transcribing');
  }

  #setDraft(value) {
    if (Object.is(this.#draft, value)) return;
    this.#draft = value;
    this.#updateBindings('draft');
  }

  #setRecorder(value) {
    if (Object.is(this.#recorder, value)) return;
    this.#recorder = value;
    this.#updateBindings('recorder');
  }

  #setChunks(value) {
    if (Object.is(this.#chunks, value)) return;
    this.#chunks = value;
    this.#updateBindings('chunks');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-disabled')) this.disabled = this.dataset.disabled;
    if (this.hasAttribute('data-running')) this.running = this.dataset.running;
    if (this.hasAttribute('data-placeholder')) this.placeholder = this.dataset.placeholder;
    if (this.hasAttribute('data-models')) this.models = this.dataset.models;
    if (this.hasAttribute('data-selected-model')) this.selectedModel = this.dataset.selectedModel;
    if (this.hasAttribute('data-selected-model-label')) this.selectedModelLabel = this.dataset.selectedModelLabel;
    if (this.hasAttribute('data-selected-model-reasoning-effort')) this.selectedModelReasoningEffort = this.dataset.selectedModelReasoningEffort;
    if (this.hasAttribute('data-attachments')) this.attachments = this.dataset.attachments;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.disabled = props.disabled;
      this.running = props.running;
      this.placeholder = props.placeholder;
      this.models = props.models;
      this.selectedModel = props.selectedModel;
      this.selectedModelLabel = props.selectedModelLabel;
      this.selectedModelReasoningEffort = props.selectedModelReasoningEffort;
      this.attachments = props.attachments;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#disabledSet) this.disabled = this.dataset.disabled;
    if (!this.#runningSet) this.running = this.dataset.running;
    if (!this.#placeholderSet) this.placeholder = this.dataset.placeholder;
    if (!this.#modelsSet) this.models = this.dataset.models;
    if (!this.#selectedModelSet) this.selectedModel = this.dataset.selectedModel;
    if (!this.#selectedModelLabelSet) this.selectedModelLabel = this.dataset.selectedModelLabel;
    if (!this.#selectedModelReasoningEffortSet) this.selectedModelReasoningEffort = this.dataset.selectedModelReasoningEffort;
    if (!this.#attachmentsSet) this.attachments = this.dataset.attachments;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'disabled':
      case 'data-disabled':
        this.disabled = newValue;
        break;
      case 'running':
      case 'data-running':
        this.running = newValue;
        break;
      case 'placeholder':
      case 'data-placeholder':
        this.placeholder = newValue;
        break;
      case 'models':
      case 'data-models':
        this.models = newValue;
        break;
      case 'selectedmodel':
      case 'data-selected-model':
        this.selectedModel = newValue;
        break;
      case 'selectedmodellabel':
      case 'data-selected-model-label':
        this.selectedModelLabel = newValue;
        break;
      case 'selectedmodelreasoningeffort':
      case 'data-selected-model-reasoning-effort':
        this.selectedModelReasoningEffort = newValue;
        break;
      case 'attachments':
      case 'data-attachments':
        this.attachments = newValue;
        break;
    }
  }

  #initState() {
    this.#recording = false;
    this.#transcribing = false;
    this.#draft = "";
    this.#recorder = false;
    this.#chunks = [];
  }

  #render() {
    const blocked = (this.#disabled || this.#running) || this.#transcribing;
    const hasDraft = this.#draft.trim() !== "";
    const actionDisabled = this.#transcribing || ((!this.#recording) && (this.#disabled || hasPendingUpload(this.#attachments)));
    return `<style>${this.#escText(styles)}</style><!--argon-text:0-->${(this.#attachments.length > 0) ? `<div class="attachments" role="group" aria-label="Attached files" tabIndex="0">${this.#attachments.map((attachment, __argonIndex) => this.#mapEventItem(`<app-attachment state="${this.#escAttr(attachment.state)}" size="sm" data-title="${this.#attrEsc(attachment.name)}" data-description="${this.#attrEsc(attachmentDescription(attachment))}">${""}<icon-file slot="media" class="attachment-file-icon">${""}</icon-file><app-button slot="actions" class="attachment-remove" variant="ghost" size="icon-xs" aria-label="${this.#escAttr(`Remove ${attachment.name}`)}" title="${this.#escAttr(`Remove ${attachment.name}`)}" data-argon="9089">${""}<icon-x>${""}</icon-x></app-button></app-attachment>`, "initial:1:9089:" + __argonIndex, { 9089: () => this.dispatchEvent(new CustomEvent("attachment-remove", { bubbles: true, composed: true, detail: { key: attachment.key } })) }, ["9089"])).join("")}</div>` : ""}<!--/argon-text:0--><form data-argon="9328"><input type="file" multiple style="display:none" data-argon="9624"><textarea data-argon-ref="input" placeholder="${this.#escAttr(this.#placeholder)}"${blocked ? ' disabled' : ''} rows="2" data-argon="10056" data-argon-bind="0"></textarea><div class="toolbar"><app-button class="attachment" size="icon-lg" variant="secondary" aria-label="Add attachment" title="Add attachment" data-argon="10812"><!--argon-text:1-->${""}<!--/argon-text:1--><icon-plus><!--argon-text:2-->${""}<!--/argon-text:2--></icon-plus></app-button><app-model-picker data-models="${this.#attrEsc(this.#models)}" data-value="${this.#attrEsc(this.#selectedModel)}" data-label="${this.#attrEsc(this.#selectedModelLabel)}" data-reasoning-effort="${this.#attrEsc(this.#selectedModelReasoningEffort)}" data-disabled="${this.#attrEsc(blocked)}" data-argon="11110" data-argon-bind="1"><!--argon-text:3-->${""}<!--/argon-text:3--></app-model-picker><div class="${this.#escAttr(`right-actions primary-action${this.#recording ? " recording" : ""}${((hasDraft && (!this.#running)) && (!this.#recording)) ? " ready" : ""}`)}" data-argon-bind="2"><app-button size="icon-lg"${actionDisabled ? ' disabled' : ''} aria-label="${this.#escAttr(this.#running ? "Stop response" : (this.#recording ? "Stop recording" : (hasDraft ? "Send message" : "Record voice message")))}" aria-pressed="${this.#escAttr(this.#recording ? "true" : "false")}" data-argon="11649" data-argon-bind="3"><!--argon-text:4-->${""}${(this.#running || this.#recording) ? `<icon-stop>${""}</icon-stop>` : hasDraft ? `<icon-arrow-up>${""}</icon-arrow-up>` : `<icon-mic>${""}</icon-mic>`}<!--/argon-text:4--></app-button></div></div></form>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#attr2 = root.querySelector('[data-argon-bind="2"]');
    this.#attr3 = root.querySelector('[data-argon-bind="3"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    if (this.#selectedModel) {
        this.setAttribute("data-selected-model", this.#selectedModel);
    }
    else {
        this.removeAttribute("data-selected-model");
    }
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const blocked = (prop === undefined || prop === 'models' || prop === 'selectedModel' || prop === 'selectedModelLabel' || prop === 'selectedModelReasoningEffort' || prop === 'disabled' || prop === 'running' || prop === 'transcribing') ? ((this.#disabled || this.#running) || this.#transcribing) : undefined;
    const hasDraft = (prop === undefined || prop === 'running' || prop === 'recording' || prop === 'draft') ? (this.#draft.trim() !== "") : undefined;
    const actionDisabled = (prop === undefined || prop === 'transcribing' || prop === 'recording' || prop === 'disabled' || prop === 'attachments') ? (this.#transcribing || ((!this.#recording) && (this.#disabled || hasPendingUpload(this.#attachments)))) : undefined;
    if (prop === undefined || prop === 'attachments') {
      const __argonNext = `${(this.#attachments.length > 0) ? `<div class="attachments" role="group" aria-label="Attached files" tabIndex="0">${this.#attachments.map((attachment, __argonIndex) => this.#mapEventItem(`<app-attachment state="${this.#escAttr(attachment.state)}" size="sm" data-title="${this.#attrEsc(attachment.name)}" data-description="${this.#attrEsc(attachmentDescription(attachment))}">${""}<icon-file slot="media" class="attachment-file-icon">${""}</icon-file><app-button slot="actions" class="attachment-remove" variant="ghost" size="icon-xs" aria-label="${this.#escAttr(`Remove ${attachment.name}`)}" title="${this.#escAttr(`Remove ${attachment.name}`)}" data-argon="9089">${""}<icon-x>${""}</icon-x></app-button></app-attachment>`, "0:1:9089:" + __argonIndex, { 9089: () => this.dispatchEvent(new CustomEvent("attachment-remove", { bubbles: true, composed: true, detail: { key: attachment.key } })) }, ["9089"])).join("")}</div>` : ""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined) {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'models' || prop === 'selectedModel' || prop === 'selectedModelLabel' || prop === 'selectedModelReasoningEffort' || prop === 'disabled' || prop === 'running' || prop === 'transcribing') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined || prop === 'running' || prop === 'recording' || prop === 'draft') {
      const __argonNext = `${""}${(this.#running || this.#recording) ? `<icon-stop>${""}</icon-stop>` : hasDraft ? `<icon-arrow-up>${""}</icon-arrow-up>` : `<icon-mic>${""}</icon-mic>`}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'placeholder') && this.#attr0) {
      const __argonNext = `${this.#placeholder}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('placeholder', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'disabled' || prop === 'running' || prop === 'transcribing') && this.#attr0) {
      this.#attr0.toggleAttribute('disabled', !!(blocked));
    }
    if ((prop === undefined || prop === 'models') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(this.#models)}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr1.setAttribute('data-models', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'selectedModel') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(this.#selectedModel)}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr1.setAttribute('data-value', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'selectedModelLabel') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(this.#selectedModelLabel)}`;
      if (__argonNext !== this.#attrLast4) {
        this.#attrLast4 = __argonNext;
        this.#attr1.setAttribute('data-label', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'selectedModelReasoningEffort') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(this.#selectedModelReasoningEffort)}`;
      if (__argonNext !== this.#attrLast5) {
        this.#attrLast5 = __argonNext;
        this.#attr1.setAttribute('data-reasoning-effort', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'disabled' || prop === 'running' || prop === 'transcribing') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(blocked)}`;
      if (__argonNext !== this.#attrLast6) {
        this.#attrLast6 = __argonNext;
        this.#attr1.setAttribute('data-disabled', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'recording' || prop === 'draft' || prop === 'running') && this.#attr2) {
      const __argonNext = `${`right-actions primary-action${this.#recording ? " recording" : ""}${((hasDraft && (!this.#running)) && (!this.#recording)) ? " ready" : ""}`}`;
      if (__argonNext !== this.#attrLast7) {
        this.#attrLast7 = __argonNext;
        this.#attr2.setAttribute('class', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'transcribing' || prop === 'recording' || prop === 'disabled' || prop === 'attachments') && this.#attr3) {
      this.#attr3.toggleAttribute('disabled', !!(actionDisabled));
    }
    if ((prop === undefined || prop === 'running' || prop === 'recording' || prop === 'draft') && this.#attr3) {
      const __argonNext = `${this.#running ? "Stop response" : (this.#recording ? "Stop recording" : (hasDraft ? "Send message" : "Record voice message"))}`;
      if (__argonNext !== this.#attrLast9) {
        this.#attrLast9 = __argonNext;
        this.#attr3.setAttribute('aria-label', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'recording') && this.#attr3) {
      const __argonNext = `${this.#recording ? "true" : "false"}`;
      if (__argonNext !== this.#attrLast10) {
        this.#attrLast10 = __argonNext;
        this.#attr3.setAttribute('aria-pressed', __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'selectedModel')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '9328').forEach(el => this.#listen(el, '9328', 'submit', (event: SubmitEvent) => {
    event.preventDefault();
    if (submitPrompt(this, root.querySelector("textarea")!, hasPendingUpload(this.#attachments)))
        this.#setDraft("");
}));
    this.#eventTargets(scope, '9624').forEach(el => this.#listen(el, '9624', 'change', (event: Event) => {
    const picker = event.target as HTMLInputElement;
    const files = Array.from(picker.files ?? []);
    picker.value = "";
    if (files.length === 0)
        return;
    this.dispatchEvent(new CustomEvent("files-attach", { bubbles: true, composed: true, detail: { files } }));
}));
    this.#eventTargets(scope, '10056').forEach(el => this.#listen(el, '10056', 'input', (event: Event) => {
    const textarea = event.currentTarget as HTMLTextAreaElement;
    this.#setDraft(textarea.value);
    textarea.style.height = "";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 220)}px`;
}));
    this.#eventTargets(scope, '10056').forEach(el => this.#listen(el, '10056', 'keydown', (event: KeyboardEvent) => {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing)
        return;
    event.preventDefault();
    if (submitPrompt(this, event.currentTarget as HTMLTextAreaElement, hasPendingUpload(this.#attachments)))
        this.#setDraft("");
}));
    this.#eventTargets(scope, '10812').forEach(el => this.#listen(el, '10812', 'click', () => root.querySelector<HTMLInputElement>('input[type="file"]')!.click()));
    this.#eventTargets(scope, '11110').forEach(el => this.#listen(el, '11110', 'value-change', (event: CustomEvent<{
    value: string;
}>) => emitModelChange(this, event.detail.value)));
    this.#eventTargets(scope, '11649').forEach(el => this.#listen(el, '11649', 'click', () => {
    if (this.#running) {
        this.dispatchEvent(new CustomEvent("prompt-stop", { bubbles: true, composed: true, detail: void 0 }));
        return;
    }
    if (this.#draft.trim() !== "") {
        if (submitPrompt(this, root.querySelector("textarea")!, hasPendingUpload(this.#attachments)))
            this.#setDraft("");
        return;
    }
    if (this.#transcribing)
        return;
    if (this.#recording) {
        this.#recorder?.stop();
        return;
    }
    if (this.#disabled || this.#running)
        return;
    if (!navigator.mediaDevices?.getUserMedia ||
        typeof MediaRecorder === "undefined") {
        emitPromptError(this, "Voice input is not supported in this browser.");
        return;
    }
    void (async () => {
        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
        catch {
            emitPromptError(this, "Microphone access was denied.");
            return;
        }
        this.#chunks.length = 0;
        const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
        const nextRecorder = mime
            ? new MediaRecorder(stream, { mimeType: mime })
            : new MediaRecorder(stream);
        this.#setRecorder(nextRecorder);
        nextRecorder.ondataavailable = (event) => {
            if (event.data.size > 0)
                this.#chunks.push(event.data);
        };
        nextRecorder.onstop = () => {
            this.#setRecording(false);
            for (const track of stream.getTracks())
                track.stop();
            const type = nextRecorder.mimeType || "audio/webm";
            this.#setRecorder(false);
            const blob = new Blob(this.#chunks, { type });
            this.#chunks.length = 0;
            if (blob.size === 0)
                return;
            this.#setTranscribing(true);
            const ext = type.includes("ogg") ? "ogg" : "webm";
            void transcribeAudio(blob, `voice.${ext}`)
                .then((text) => {
                this.#setDraft(insertTranscript(root as ShadowRoot, text));
            })
                .catch((error) => emitPromptError(this, error instanceof Error && error.message
                ? error.message
                : "Could not transcribe the recording."))
                .finally(() => {
                this.#setTranscribing(false);
            });
        };
        nextRecorder.start();
        this.#setRecording(true);
    })();
}));
    this.#eventTargets(scope, '9089').forEach(el => this.#listen(el, '9089', 'click', () => this.dispatchEvent(new CustomEvent("attachment-remove", { bubbles: true, composed: true, detail: { key: attachment.key } }))));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
